import React, { FC, useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useMediaQuery } from 'react-responsive';
import { Icon, Toast } from 'src/components';
import { DropdownSectionProps } from 'src/components/DropdownMenu';
import theme from 'src/utils/theme';
import { useIntersection } from 'src/hooks';
import { UserDictionaryApi } from 'src/api';
import { useUserContext } from 'src/context/user';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import { WordDetailsModal } from 'src/apps/ScenarioGeneric/WordDetailsModal';
import { blurFromAllElements } from 'src/utils/browser';
import { resolveLearnableImageUrl } from 'src/utils/learnableMedia';
import * as S from './styles';

const msgs = defineMessages({
  markAsKnown: { id: 'scenarioDetails_markWordAsKnown' },
  unmarkAsKnown: { id: 'scenarioDetails_unmarkWordAsKnown' },
  markAsDifficult: { id: 'scenarioDetails_markWordAsDifficult' },
  unmarkAsDifficult: { id: 'scenarioDetails_removeWordAsDifficult' },
  wordInformation: { id: 'words_detail_word_info' },
  upgradeToAccess: { id: 'words_detail_upgrade' },
  audio: { id: 'words_detail_audio' },
});

interface Props {
  growthLevel: number;
  targetValue: string;
  sourceValue: string;
  alreadyKnown: boolean;
  difficult: boolean;
  learnableId: number;
  isLocked?: boolean;
  difficultDisabled?: boolean;
  dueForReview?: boolean;
  refetchUserScenarioDetails?: () => void;
  rootElement?: HTMLElement | null;
  startAudio?: () => void;
  stopAudio?: () => void;
  isPlayingAudio?: boolean;
  targetLanguageName?: string;
  sourceLanguageName?: string;
  activeToastId?: number | null;
  setActiveToastId?: (id: number | null) => void;
}

export const LearnablesListElement: FC<Props> = ({
  growthLevel,
  targetValue,
  sourceValue,
  alreadyKnown,
  difficult,
  learnableId,
  isLocked,
  difficultDisabled,
  dueForReview,
  refetchUserScenarioDetails,
  rootElement,
  startAudio,
  stopAudio,
  isPlayingAudio,
  targetLanguageName,
  sourceLanguageName,
  activeToastId,
  setActiveToastId,
}) => {
  const intl = useIntl();
  const isMobile = useMediaQuery({ maxWidth: theme.sz.m });
  const languagePairId = getLanguagePairId();
  const { user } = useUserContext();
  const [isDifficult, setAsDifficult] = useState(difficult);
  const [isMarkedAsKnown, setAsKnown] = useState(alreadyKnown);
  // if a learnable is unignored, the growthLevel is set to max(6, growthLevel) in the back-end
  const [refreshedGrowthLevel, setGrowthLevel] = useState(growthLevel);
  const positionRef = useRef(null);
  // to display 3 dots menu in the modal
  const inViewport = useIntersection(positionRef, '0px 0px -190px 0px', rootElement);
  const [isWordDetailsModalOpen, setIsWordDetailsModalOpen] = useState(false);

  useEffect(() => {
    setAsKnown(alreadyKnown);
  }, [alreadyKnown]);

  useEffect(() => {
    setAsDifficult(difficult);
  }, [difficult]);

  useEffect(() => {
    setGrowthLevel(growthLevel);
  }, [growthLevel]);

  const styledProps = {
    isIconOnRight: true,
    fontSize: theme.DEPRECATED_ty.medium,
    fontWeight: theme.DEPRECATED_ty.mediumWeight,
  };

  const handleIgnoreToggle = async (learnableId: number, currentlyIgnored: boolean) => {
    try {
      if (currentlyIgnored) {
        languagePairId &&
          (await UserDictionaryApi.updateSingleLearnable(languagePairId, learnableId, {
            known: false,
          }));
        setGrowthLevel(6);
      } else {
        if (languagePairId) {
          await UserDictionaryApi.updateSingleLearnable(languagePairId, learnableId, {
            known: true,
          });
        }
      }
      if (refetchUserScenarioDetails) {
        refetchUserScenarioDetails();
      }
    } catch (error) {
      /* istanbul ignore next: unexpected error */
      console.error({ error });
    }
    setAsKnown(!isMarkedAsKnown);
  };

  const handleDifficultToggle = async (learnableId: number, currentlyDifficult: boolean) => {
    try {
      if (currentlyDifficult) {
        languagePairId &&
          (await UserDictionaryApi.updateSingleLearnable(languagePairId, learnableId, {
            difficult: false,
          }));
      } else {
        languagePairId &&
          (await UserDictionaryApi.updateSingleLearnable(languagePairId, learnableId, {
            difficult: true,
          }));
      }
      if (refetchUserScenarioDetails) {
        refetchUserScenarioDetails();
      }
    } catch (error) {
      /* istanbul ignore next: unexpected error */
      console.error({ error });
    }
    setAsDifficult(!isDifficult);
  };

  const handleWordClick = () => {
    if (isLocked && !user?.isPro && setActiveToastId) {
      setActiveToastId(learnableId);
      setIsWordDetailsModalOpen(false);
    } else {
      setIsWordDetailsModalOpen(true);
    }
  };
  const handleCloseWordDetails = () => setIsWordDetailsModalOpen(false);

  const handleKeyUp = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleWordClick();
      blurFromAllElements();
    }
  };

  const handleRowClick = () => {
    if (isLocked && !user?.isPro && setActiveToastId) {
      setActiveToastId(learnableId);
    }
    blurFromAllElements();
  };

  const dropdownSections = [
    {
      dropdownItems: [
        {
          type: 'icon',
          iconCode: 'sentencesOutline',
          labelText: intl.formatMessage(msgs.wordInformation),
          onClick: () => handleWordClick(),
          ...styledProps,
        },
      ],
    },
    {
      dropdownItems: [
        {
          type: 'icon',
          iconCode: isMarkedAsKnown ? 'tickFilled' : 'tickOutline',
          labelText: intl.formatMessage(isMarkedAsKnown ? msgs.unmarkAsKnown : msgs.markAsKnown),
          onClick: async () => {
            await handleIgnoreToggle(learnableId, isMarkedAsKnown);
          },
          ...styledProps,
        },
      ],
    },
    ...(isMarkedAsKnown || difficultDisabled
      ? []
      : [
          {
            dropdownItems: [
              {
                type: 'icon',
                iconCode: isDifficult ? 'difficultWordsFilled' : 'difficultWordsOutline',
                labelText: intl.formatMessage(
                  isDifficult ? msgs.unmarkAsDifficult : msgs.markAsDifficult,
                ),
                onClick: async () => {
                  await handleDifficultToggle(learnableId, isDifficult);
                },
                ...styledProps,
              },
            ],
          },
        ]),
  ];

  const imageUrl = resolveLearnableImageUrl(targetValue);

  const targetSourceText = (
    <>
      <S.TargetText onClick={() => handleWordClick()} onKeyUp={handleKeyUp} tabIndex={0} dir="auto">
        {imageUrl ? <S.TargetImage src={imageUrl} alt={sourceValue} /> : targetValue}
      </S.TargetText>
      <S.SourceText onClick={() => handleWordClick()} onKeyUp={handleKeyUp} tabIndex={0} dir="auto">
        {sourceValue}
      </S.SourceText>
    </>
  );

  return (
    <li>
      <S.RowWrapper
        key={targetValue}
        onClick={handleRowClick}
        onKeyUp={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleRowClick();
          }
        }}
        tabIndex={0}
        data-testid="learnable_row"
      >
        <S.GrowthLevelIndicator
          growthLevel={refreshedGrowthLevel}
          alreadyKnown={isMarkedAsKnown}
          difficult={isDifficult}
          isItemDueForReview={!!dueForReview}
        />
        {isMobile ? (
          <S.TextWrapper
            onClick={() => handleWordClick()}
            onKeyUp={handleKeyUp}
            tabIndex={0}
            data-testid="targetSourceWrapper"
          >
            {targetSourceText}
          </S.TextWrapper>
        ) : (
          targetSourceText
        )}
        <S.IconsWrapper>
          {startAudio && stopAudio && (
            <S.AudioButton
              $isPlaying={isPlayingAudio}
              onClick={isPlayingAudio ? stopAudio : startAudio}
              onKeyUp={handleKeyUp}
              tabIndex={0}
              type="button"
              aria-label={intl.formatMessage(msgs.audio)}
            >
              <Icon name={isPlayingAudio ? 'wave' : 'speakerVolume3'} />
            </S.AudioButton>
          )}
          {!isLocked && (
            <S.EllipsisPopout
              positionX="left"
              positionY={!inViewport ? 'top' : 'bottom'}
              dropdownSections={dropdownSections as DropdownSectionProps[]}
            />
          )}
        </S.IconsWrapper>
        {activeToastId === learnableId && (
          <S.ToastWrapper>
            <Toast
              status="info"
              snackBar
              pointedTip="left"
              shouldCloseOnOutsideClick
              onClick={() => setActiveToastId && setActiveToastId(null)}
            >
              {intl.formatMessage(msgs.upgradeToAccess)}
            </Toast>
          </S.ToastWrapper>
        )}
      </S.RowWrapper>
      <div ref={positionRef} />
      {isWordDetailsModalOpen && (
        <WordDetailsModal
          onClose={handleCloseWordDetails}
          isKnown={isMarkedAsKnown}
          isDifficult={isDifficult}
          hideDifficultToggle={difficultDisabled}
          onMarkAsKnown={handleIgnoreToggle}
          onMarkAsDifficult={handleDifficultToggle}
          targetLanguageName={targetLanguageName}
          sourceLanguageName={sourceLanguageName}
          learnableId={learnableId}
        />
      )}
    </li>
  );
};
